// pages/superadmin/RevenueForecastDashboard.jsx
//
// Phase 5C, Revenue Forecasting. Renders forecastingService's two
// independent estimates:
//   1. Trend forecast, trailing-month moving average of logged
//      deal_won outcomes, projected one month forward.
//   2. Pipeline-weighted forecast, open pipeline stage counts x
//      historical won-rate x average deal size.
// Neither number is shown without its confidence/sample-size context,
// mirrors AiPerformanceDashboard's trackingAvailable / estimatedRoi
// treatment, so a bare number is never presented as more certain than
// the underlying data supports. Rendered as the "Forecast" tab inside
// CustomerDashboard. Scoping (internal vs one partner's own figures)
// happens server-side in forecastingController, so this component has no
// role-specific logic.
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import useRevenueApi from "../../../hooks/useRevenueApi";
import { LineChart } from "../../../arc/components/line-chart/line-chart";
import { DonutChart } from "../../../arc/components/donut-chart/donut-chart";
import { Skeleton } from "../../../arc/components/skeleton/skeleton";
import { ThemeSwitch } from "../../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../../utils/themeTransition";
import InfoTooltip from "../../../components/InfoTooltip";
import useSubnavSlot from "../../../components/useSubnavSlot";
import { createPortal } from "react-dom";
import { scopeCss } from "../../../utils/scopeCss";

const EMBED_SCOPE = ".ri-embed-forecast";

const styles = `
  /* Design tokens: Cal.com system (DESIGN-cal.md). Defined here so the
   * component never depends on variables from a parent stylesheet. */
  .dash-root {
    --bg: #ffffff;
    --surface: #ffffff;
    --surface-sunken: #f5f5f5;
    --border: #e5e7eb;
    --text: #111111;
    --text-body: #374151;
    --text-muted: #6b7280;
    --text-faint: #898989;
    --ink: #111111;
    --on-ink: #ffffff;
    --green: #10b981;
    --amber: #f59e0b;
    --red: #ef4444;
    --radius: 8px;
    --radius-lg: 12px;
    --sans: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    --display: "Cal Sans", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    --surface-pressed: #f5f5f5;
    color-scheme: light;
    --shadow-sm: 0 1px 2px rgba(0,0,0,0.05);
    /* Arc component tokens (metric-card, bar-chart), mapped onto Cal */
    --foreground: var(--text);
    --text-secondary: var(--text-body);
    --border-strong: #d1d5db;
    --accent: #111111;
    --success: var(--green);
    --danger: var(--red);
    --font-body: var(--sans);
    --font-display: var(--display);
    --space-1: 4px; --space-2: 8px; --space-3: 12px; --space-4: 16px; --space-5: 24px; --space-6: 32px;
    --radius-surface: 12px; --radius-control: 8px; --radius-pill: 9999px;
    --shadow-resting: 0 1px 2px rgba(0,0,0,0.05);
    --text-xs: 12px; --text-sm: 13px; --text-base: 14px; --text-md: 16px; --text-3xl: 36px;
    --leading-body: 1.5; --leading-display: 1.15;
    --tracking-display: -1px; --tracking-body: 0;
    --duration-fast: 150ms; --ease-standard: cubic-bezier(0.25, 1, 0.5, 1);
  }
  /* dark: elegant cool-neutral ladder, matching design-tokens.css */
  .dash-root.dark {
    --bg: #0b0d11;
    --surface: #14171c;
    --surface-raised: #1b1f26;
    --surface-sunken: #0f1217;
    --border: #262b33;
    --text: #f3f5f8;
    --text-body: #c9d0da;
    --text-muted: #9aa3b2;
    --text-faint: #6a7280;
    --ink: #ffffff;
    --on-ink: #111111;
    --shadow-sm: none;
    --surface-pressed: #1b1f26;
    color-scheme: dark;
    --border-strong: #363c46;
    --accent: #ffffff;
    --shadow-resting: none;
  }

  /* Only box-sizing is global. A blanket margin/padding reset ties in specificity
   * with Arc's CSS-module classes once scoped, and wipes their spacing (counter
   * feather padding, badge margin). Reset only the elements this file owns. */
  *, *::before, *::after { box-sizing: border-box; }
  .dash-brand-name, .section-title, .section-caption, .table-wrap { margin: 0; padding: 0; }
  html, body { margin: 0; overflow-x: hidden; }
  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased; overflow-x: clip; }

  /* NAV: white canvas, 64px, hairline underneath */
  .dash-header { background: var(--bg); border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1200px; margin: 0 auto; padding: 0 28px; min-height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .dash-brand { display: flex; align-items: center; gap: 12px; padding: 12px 0; min-width: 0; }
  .btn-back, .dash-brand-mark { display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px; flex-shrink: 0; border: 1px solid var(--border); border-radius: 9999px; background: var(--surface); color: var(--text); }
  .btn-back { text-decoration: none; }
  .btn-back:active { background: var(--surface-pressed); }
  .btn-back:focus-visible, .btn-header:focus-visible, .btn-retry:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }
  .dash-brand-mark { border-radius: var(--radius); background: var(--surface-sunken); border-color: transparent; }
  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .dash-brand-name { font-size: 14px; font-weight: 600; color: var(--text); line-height: 1.3; }
  .dash-brand-sub { font-size: 13px; color: var(--text-muted); line-height: 1.3; }
  .dash-header-actions { display: flex; align-items: center; gap: 8px; padding: 12px 0; }

  /* BUTTONS: secondary (white, hairline, 8px). Pressed darkens, nothing else. */
  .btn-header, .btn-retry { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 36px; padding: 0 16px; background: var(--surface); border: 1px solid var(--border); border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; line-height: 1; color: var(--text); cursor: pointer; white-space: nowrap; text-decoration: none; }
  .btn-header:active:not(:disabled) { background: var(--surface-pressed); }
  .btn-header:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-header svg { flex-shrink: 0; }
  .btn-header.is-refreshing svg { animation: refresh-spin 0.6s linear; }
  @keyframes refresh-spin { to { transform: rotate(360deg); } }
  .btn-retry { background: var(--ink); border-color: var(--ink); color: var(--on-ink); margin-top: 6px; }
  .btn-retry:active { opacity: 0.85; }

  .dash-main { max-width: 1200px; margin: 0 auto; padding: 48px 28px 96px; display: flex; flex-direction: column; gap: 64px; min-width: 0; }
  .dash-main > section { animation: card-in 0.25s ease-out both; }
  @keyframes card-in { from { opacity: 0; } to { opacity: 1; } }

  /* SECTIONS: Cal Sans heading, tight tracking, caption beneath */
  .section-head { display: flex; flex-direction: column; gap: 4px; margin-bottom: 20px; }
  .section-title-row { display: flex; align-items: center; gap: 8px; }
  .section-title { display: inline-flex; align-items: center; gap: 4px; font-family: var(--display); font-size: 28px; font-weight: 600; line-height: 1.2; letter-spacing: -0.5px; color: var(--text); }
  .section-caption { font-size: 14px; color: var(--text-muted); max-width: 65ch; }

  /* METRIC CARDS: white + hairline; grey fill marks the headline figure */
  .metric-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
  .metric-trend { display: flex; flex-direction: column; gap: 8px; }
  /* Card padding: one even inset on every card so labels and values align across a row. The headline card is marked by its grey fill alone. */
  .dash-root .metric-grid > article { padding: 24px; }
  .dash-root .ri-hero { background: var(--surface-sunken); border-color: transparent; }
  .dash-root.dark .ri-hero { border-color: var(--border); }

  /* BADGE: neutral pill, a dot carries the status */
  .badge { display: inline-flex; align-items: center; gap: 6px; font-family: var(--sans); font-size: 13px; font-weight: 500; padding: 4px 12px; border-radius: 9999px; background: var(--surface); border: 1px solid var(--border); color: var(--text); white-space: nowrap; }
  .badge::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--dot, var(--text-faint)); }
  .ri-hero .badge { background: var(--bg); }
  .badge.confidence-moderate, .badge.direction-growing { --dot: var(--green); }
  .badge.confidence-low { --dot: var(--amber); }
  .badge.direction-declining { --dot: var(--red); }
  .badge.confidence-insufficient_data, .badge.direction-flat { --dot: var(--text-faint); color: var(--text-muted); }

  /* CHART */
  .chart-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 32px; margin-top: 16px; box-shadow: var(--shadow-sm); }

  /* STAGE LIST */
  .table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; margin-top: 16px; list-style: none; }
  .breakdown-row { display: flex; align-items: center; gap: 16px; padding: 20px 24px; border-bottom: 1px solid var(--border); }
  .breakdown-row:last-child { border-bottom: none; }
  .breakdown-main { flex: 1 1 auto; min-width: 0; }
  .breakdown-task { font-size: 14px; font-weight: 500; color: var(--text); }
  .breakdown-bar-track { width: 100%; height: 4px; border-radius: 9999px; background: var(--border); margin-top: 10px; overflow: hidden; }
  .dash-root.dark .breakdown-bar-track { background: var(--border-strong); }
  .breakdown-bar-fill { height: 100%; border-radius: 9999px; background: var(--ink); transform-origin: left; animation: bar-grow 0.4s cubic-bezier(0.25, 1, 0.5, 1) both; }
  @keyframes bar-grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
  .breakdown-cost { flex-shrink: 0; width: 8ch; text-align: right; font-size: 14px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }

  .notice-card { display: flex; align-items: flex-start; gap: 10px; background: var(--surface-sunken); border-radius: var(--radius); padding: 16px 20px; margin-top: 16px; font-size: 14px; line-height: 1.5; color: var(--text-body); }
  .dash-root.dark .notice-card { border: 1px solid var(--border); }
  .notice-card svg { flex-shrink: 0; color: var(--amber); margin-top: 3px; }

  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 20px; gap: 12px; text-align: center; }
  .empty-state-label { font-size: 14px; color: var(--text-muted); }
  .spinner { width: 24px; height: 24px; border: 2px solid var(--border); border-top-color: var(--ink); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Demo-parity layout */
  .page-head { display: flex; flex-direction: column; gap: 5px; }
  .page-eyebrow { display: inline-flex; align-items: center; gap: 6px; align-self: flex-start; padding: 3px 10px; border-radius: 9999px; background: var(--surface-sunken); font-size: 12px; font-weight: 500; color: var(--text-muted); }
  .page-title { font-family: var(--sans); font-size: 18px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.4; color: var(--text); }
  .page-sub { font-size: 13px; line-height: 1.55; color: var(--text-muted); max-width: 70ch; }
  .forecast-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
  .forecast-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px 22px; min-width: 0; }
  .forecast-value { font-family: "Cal Sans", var(--sans); font-size: 28px; font-weight: 600; letter-spacing: -0.5px; line-height: 1.2; color: var(--text); margin: 10px 0 6px; }
  .forecast-method { font-size: 12.5px; line-height: 1.55; color: var(--text-muted); }
  .forecast-inputs { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin-top: 14px; }
  .forecast-input { background: var(--surface-sunken); border-radius: var(--radius); padding: 9px 11px; min-width: 0; }
  .forecast-input .k { font-size: 10px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-faint); }
  .forecast-input .v { margin-top: 3px; font-size: 14px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }
  .two-col { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); gap: 16px; }
  .card-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin-bottom: 14px; }
  .card-title { font-size: 14px; font-weight: 600; color: var(--text); }
  .card-note { font-size: 12px; color: var(--text-faint); }
  .chart-bars { display: flex; align-items: flex-end; gap: 12px; height: 190px; padding-top: 8px; }
  .chart-bar-col { flex: 1 1 0; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; min-width: 0; gap: 8px; }
  .chart-bar-track { width: 100%; flex: 1 1 auto; display: flex; align-items: flex-end; }
  .chart-bar-fill { width: 100%; border-radius: 4px 4px 0 0; background: var(--ink); min-height: 3px; }
  .chart-bar-value { font-size: 12px; font-weight: 500; color: var(--text-muted); white-space: nowrap; font-variant-numeric: tabular-nums; }
  .chart-bar-label { font-size: 12px; color: var(--text-muted); white-space: nowrap; }
  .data-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .data-table th { text-align: left; padding: 10px 14px; font-family: var(--sans); font-size: 12px; font-weight: 500; color: var(--text-muted); background: var(--surface-raised); border-bottom: 1px solid var(--border); white-space: nowrap; }
  .data-table td { padding: 11px 14px; border-bottom: 1px solid var(--border); color: var(--text-muted); vertical-align: middle; }
  .data-table tbody tr:last-child td { border-bottom: none; }
  .data-table tbody tr:hover td { background: var(--surface-raised); }
  .data-table .strong { color: var(--text); font-weight: 600; }
  .data-table .num { font-family: var(--mono); text-align: right; color: var(--text); font-variant-numeric: tabular-nums; }
  @media (max-width: 900px) { .two-col, .forecast-grid { grid-template-columns: 1fr; } }

  @media (max-width: 1024px) { .dash-main { gap: 56px; } }
  @media (max-width: 640px) {
    .dash-header-inner { padding: 8px 16px; }
    .dash-brand, .dash-header-actions { padding: 4px 0; }
    .dash-main { padding: 32px 16px 56px; gap: 48px; }
    .section-title { font-size: 22px; letter-spacing: -0.3px; }
    .metric-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
    .btn-header { padding: 0; width: 40px; }
    .btn-header span.btn-label { display: none; }
    .dash-root .metric-grid > article { padding: 16px; }
    .chart-card { padding: 20px 16px; }
    .breakdown-row { padding: 16px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .dash-main > section, .breakdown-bar-fill, .btn-header.is-refreshing svg { animation: none; }
  }
`;

const STAGE_LABELS = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  proposal: "Proposal",
};

const STAGE_COLORS = {
  new: "var(--series-1)",
  contacted: "var(--series-2)",
  qualified: "var(--series-3)",
  proposal: "var(--series-4)",
};

const CONFIDENCE_LABELS = {
  moderate: "Moderate confidence",
  low: "Low confidence",
  insufficient_data: "Insufficient data",
};

const DIRECTION_LABELS = {
  growing: "Growing",
  declining: "Declining",
  flat: "Flat",
};

function formatGbp(value) {
  return `£${Math.round(value).toLocaleString()}`;
}

function formatCount(value) {
  return Number(value).toLocaleString("en-GB");
}

function monthLabel(monthKey) {
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-GB", {
    month: "short",
  });
}

function monthLong(monthKey) {
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
}

export default function RevenueForecastDashboard({
  backTo = "/super-admin",
  embedded = false,
  darkMode: hostDarkMode,
}) {
  const { logout } = useAuth();
  const api = useRevenueApi();
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Stored choice wins; otherwise follow the OS. Storage can throw (private
  // mode, blocked cookies), so every access is guarded.
  const [ownDarkMode, setDarkMode] = useState(() => {
    try {
      const stored = localStorage.getItem("axo_theme");
      if (stored) return stored === "dark";
      return (
        window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false
      );
    } catch {
      return false;
    }
  });

  // Embedded in CustomerDashboard: the host owns the theme.
  const followsHost = embedded && hostDarkMode !== undefined;
  const darkMode = followsHost ? hostDarkMode : ownDarkMode;

  useEffect(() => {
    if (followsHost) return;
    try {
      localStorage.setItem("axo_theme", darkMode ? "dark" : "light");
    } catch {
      /* theme still applies for this session */
    }
  }, [darkMode, followsHost]);

  // Keep other tabs (and an embedding host that writes the same key) in sync.
  useEffect(() => {
    if (followsHost) return undefined;
    const onStorage = (e) => {
      if (e.key === "axo_theme" && e.newValue)
        setDarkMode(e.newValue === "dark");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [followsHost]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getForecast();
      setForecast(res.data);
    } catch (err) {
      setError("Couldn't load the revenue forecast");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const trend = forecast?.trend;
  const pipeline = forecast?.pipeline;
  const subnavSlot = useSubnavSlot();

  const rootInner = (
    <div
      className={`dash-root${darkMode ? " dark" : ""}`}
      data-theme={darkMode ? "dark" : "light"}
    >
      <style>
        {embedded
          ? scopeCss(styles, EMBED_SCOPE) +
            `${EMBED_SCOPE} .dash-root { min-height: 0; background: var(--bg); color: var(--text); }
             ${EMBED_SCOPE} .dash-main { max-width: none; padding: 0; gap: 48px; }
             `
          : styles}
      </style>

      {embedded &&
        subnavSlot &&
        createPortal(
          <button
            className={`btn btn-ghost btn-sm${loading ? " is-refreshing" : ""}`}
            onClick={load}
            aria-label="Refresh"
          >
            <span className="btn-label">Refresh</span>
          </button>,
          subnavSlot,
        )}

      {!embedded && (
        <header className="dash-header">
          <div className="dash-header-inner">
            <div className="dash-brand">
              <Link
                to={backTo}
                className="btn-back"
                aria-label="Back to dashboard"
              >
                <svg
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </Link>
              <span className="dash-brand-mark" aria-hidden="true">
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 3v18h18M18 17V9M13 17V5M8 17v-4"
                  />
                </svg>
              </span>
              <div className="dash-brand-text">
                <h1 className="dash-brand-name">Revenue forecast</h1>
                <div className="dash-brand-sub">AXO Revenue Intelligence</div>
              </div>
            </div>
            <div className="dash-header-actions">
              <button
                className={`btn-header${loading ? " is-refreshing" : ""}`}
                onClick={load}
                aria-label="Refresh"
              >
                <svg
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"
                  />
                </svg>
                <span className="btn-label">Refresh</span>
              </button>
              <ThemeSwitch
                iconOnly
                theme={darkMode ? "dark" : "light"}
                onThemeChange={(next, variant, trigger) =>
                  applyThemeWithTransition(next, trigger, setDarkMode, variant)
                }
              />
              <button
                onClick={logout}
                className="btn-header"
                aria-label="Log out"
              >
                <svg
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
                  />
                </svg>
                <span className="btn-label">Log out</span>
              </button>
            </div>
          </div>
        </header>
      )}

      <div className="dash-main">
        {loading ? (
          <div
            className="empty-state"
            role="status"
            aria-live="polite"
            style={{ width: "100%" }}
          >
            <Skeleton lines={6} label="Loading forecast" />
          </div>
        ) : error || !trend || !pipeline ? (
          <div className="empty-state" role="alert">
            <div className="empty-state-label">
              {error || "Revenue forecast is unavailable"}
            </div>
            <button className="btn-retry" onClick={load}>
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="page-head">
              <span className="page-eyebrow">Revenue intelligence</span>
              <div className="page-title">Revenue Forecast</div>
              <p className="page-sub">
                Two independent forecasts, each shown with its method,
                confidence and sample size. AXO never presents a single number
                as certainty.
              </p>
            </div>

            <div className="forecast-grid">
              <div className="forecast-card">
                <div className="card-head">
                  <div className="card-title">Trend forecast</div>
                  <span className={`badge confidence-${trend.confidence}`}>
                    {CONFIDENCE_LABELS[trend.confidence] || trend.confidence}
                  </span>
                </div>
                <div className="forecast-value">
                  {trend.nextMonthForecastGbp != null
                    ? formatGbp(trend.nextMonthForecastGbp)
                    : "Not enough data"}
                </div>
                <div className="forecast-method">
                  Trailing moving average of closed-won revenue, projected one
                  month forward.
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 12,
                    flexWrap: "wrap",
                  }}
                >
                  {trend.direction && (
                    <span className={`badge direction-${trend.direction}`}>
                      {DIRECTION_LABELS[trend.direction] || trend.direction}
                    </span>
                  )}
                  <span className="card-note">
                    {trend.monthsOfData} months of won-deal history
                  </span>
                </div>
              </div>

              <div className="forecast-card">
                <div className="card-head">
                  <div className="card-title">Pipeline-weighted forecast</div>
                  <span className={`badge confidence-${pipeline.confidence}`}>
                    {CONFIDENCE_LABELS[pipeline.confidence] ||
                      pipeline.confidence}
                  </span>
                </div>
                <div className="forecast-value">
                  {pipeline.weightedPipelineGbp != null
                    ? formatGbp(pipeline.weightedPipelineGbp)
                    : "Not enough data"}
                </div>
                <div className="forecast-method">
                  Open pipeline stage counts × historical win rate × average
                  deal size.
                </div>
                <div className="forecast-inputs">
                  <div className="forecast-input">
                    <div className="k">Open opportunities</div>
                    <div className="v">{formatCount(pipeline.totalOpen)}</div>
                  </div>
                  <div className="forecast-input">
                    <div className="k">Historical win rate</div>
                    <div className="v">
                      {pipeline.overallWonRate != null
                        ? `${Math.round(pipeline.overallWonRate * 100)}%`
                        : "—"}
                    </div>
                  </div>
                  <div className="forecast-input">
                    <div className="k">Average deal size</div>
                    <div className="v">
                      {pipeline.averageDealGbp != null
                        ? formatGbp(pipeline.averageDealGbp)
                        : "—"}
                    </div>
                  </div>
                  <div className="forecast-input">
                    <div className="k">Open stages</div>
                    <div className="v">
                      {Object.keys(pipeline.openStageCounts || {}).length}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {trend.message && (
              <div className="notice-card">
                <svg
                  width="15"
                  height="15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path strokeLinecap="round" d="M12 8v5M12 16h.01" />
                </svg>
                <span>{trend.message}</span>
              </div>
            )}

            {pipeline.message && (
              <div className="notice-card">
                <svg
                  width="15"
                  height="15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path strokeLinecap="round" d="M12 8v5M12 16h.01" />
                </svg>
                <span>{pipeline.message}</span>
              </div>
            )}

            <div className="two-col">
              <div className="chart-card">
                <div className="card-head">
                  <div className="card-title">Won revenue by month</div>
                  <div className="card-note">Actual · All time</div>
                </div>
                {trend.series?.length > 0 ? (
                  <LineChart
                    label="Won revenue by month"
                    categoryLabel="Month"
                    data={trend.series.map((m) => ({
                      key: m.month,
                      label: monthLong(m.month),
                      axisLabel: monthLabel(m.month),
                      values: { revenue: m.revenueGbp },
                    }))}
                    series={[
                      {
                        key: "revenue",
                        label: "Won revenue",
                        color: "var(--purple)",
                      },
                    ]}
                    height={240}
                    formatValue={formatGbp}
                  />
                ) : (
                  <div className="chart-empty">No won revenue yet</div>
                )}
              </div>

              <div className="chart-card">
                <div className="card-head">
                  <div className="card-title">Open pipeline by stage</div>
                  <div className="card-note">
                    {formatCount(pipeline.totalOpen)} opportunities
                  </div>
                </div>
                {pipeline.totalOpen === 0 ? (
                  <div className="chart-empty">
                    No open opportunities in the pipeline
                  </div>
                ) : (
                  <DonutChart
                    data={Object.entries(pipeline.openStageCounts || {}).map(
                      ([stage, count]) => ({
                        key: stage,
                        label: STAGE_LABELS[stage] || stage,
                        value: count,
                        color: STAGE_COLORS[stage] || "var(--text-faint)",
                      }),
                    )}
                    label="Open pipeline by stage"
                    unit="opportunities"
                    totalLabel="Open"
                    size={210}
                    thickness={26}
                  />
                )}
              </div>
            </div>

            <div>
              <div className="section-head">
                <div className="section-title-row">
                  <h2
                    className="section-title"
                    style={{ fontSize: 18, letterSpacing: "-0.01em" }}
                  >
                    Pipeline-weighted contribution by stage
                  </h2>
                  <InfoTooltip
                    label="What is pipeline-weighted contribution?"
                    content="Each open stage weighted by your historical win rate. Expected deals are the number of open opportunities multiplied by the historical won rate."
                  />
                </div>
                <p className="section-caption">Open pipeline only</p>
              </div>
              <div className="table-wrap" style={{ marginTop: 0 }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Stage</th>
                      <th style={{ textAlign: "right" }}>Opportunities</th>
                      <th style={{ textAlign: "right" }}>Share</th>
                      <th style={{ textAlign: "right" }}>Expected deals</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(pipeline.openStageCounts || {}).map(
                      ([stage, count]) => (
                        <tr key={stage}>
                          <td className="strong">
                            {STAGE_LABELS[stage] || stage}
                          </td>
                          <td className="num">{formatCount(count)}</td>
                          <td className="num">
                            {pipeline.totalOpen > 0
                              ? `${Math.round((count / pipeline.totalOpen) * 100)}%`
                              : "—"}
                          </td>
                          <td className="num">
                            {pipeline.overallWonRate != null
                              ? (count * pipeline.overallWonRate).toFixed(1)
                              : "—"}
                          </td>
                        </tr>
                      ),
                    )}
                    <tr>
                      <td className="strong">Total open</td>
                      <td className="num">{formatCount(pipeline.totalOpen)}</td>
                      <td className="num">100%</td>
                      <td className="num">
                        {pipeline.overallWonRate != null
                          ? (
                              pipeline.totalOpen * pipeline.overallWonRate
                            ).toFixed(1)
                          : "—"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p
                style={{
                  marginTop: 10,
                  fontSize: 11.5,
                  lineHeight: 1.6,
                  color: "var(--text-faint)",
                }}
              >
                Weighted using the historical{" "}
                {pipeline.overallWonRate != null
                  ? `${Math.round(pipeline.overallWonRate * 100)}% win rate`
                  : "win rate (not enough data yet)"}
                . The forecast is only shown once the sample is large enough to
                be meaningful.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return embedded ? (
    <div className="ri-embed-forecast">{rootInner}</div>
  ) : (
    rootInner
  );
}
